// Modified from http://lea.verou.me/2016/12/resolve-promises-externally-with-this-one-weird-trick/

/**
 * @typedef {Object} DeferredPromise
 * @property {Promise} promise - The underlying promise
 * @property {(value?: any) => void} resolve - Function to resolve the promise
 * @property {(reason?: any) => void} reject - Function to reject the promise
 */

/**
 * Creates a deferred promise that can be resolved or rejected externally
 * @param {(resolve: (value?: any) => void, reject: (reason?: any) => void) => void} [cb] - Optional callback function called with resolve and reject functions
 * @returns {Promise & { resolve: (value?: any) => void, reject: (reason?: any) => void }} A promise with resolve and reject methods attached
 */
export function defer(cb = () => {}) {
	let res;
	let rej;

	const promise = new Promise((resolve, reject) => {
		res = resolve;
		rej = reject;
		cb(resolve, reject);
	});

	promise.resolve = res;
	promise.reject = rej;

	return promise;
}

/**
 * @typedef {Object} StableDeferOptions
 * @property {string} [id='stableDefer'] - Unique identifier for the deferred instance
 * @property {boolean} [autoStart=true] - Whether to automatically start the callback when created
 * @property {() => void} [callback] - Function to call when getValue is first invoked
 * @property {number} [timeout=10000] - Timeout in milliseconds (0 disables timeout)
 * @property {string|null} [timeoutErrorMessage=null] - Error message for timeout
 * @property {(message: string) => void} [onTimeout] - Custom timeout handler function
 */

/**
 * @typedef {Object} StableDeferredPromise
 * @property {string} id - The identifier for this deferred instance
 * @property {() => Promise<any>} getValue - Function to get the value (starts callback if not started)
 * @property {(value?: any) => void} resolve - Function to resolve the promise
 * @property {(error?: Error) => void} reject - Function to reject the promise
 * @property {Promise<any>} then - Promise then method
 * @property {Promise<any>} catch - Promise catch method
 * @property {Promise<any>} finally - Promise finally method
 */

/**
 * Creates a stable deferred promise with timeout and callback support
 * @param {string|StableDeferOptions} [messageOrOpts] - Either a timeout error message string or options object
 * @returns {StableDeferredPromise} A stable deferred promise with additional methods
 */
export const stableDefer = (messageOrOpts = stableDefer.DEFAULT_OPTS) => {
	const { id, autoStart, callback, timeout, timeoutErrorMessage, onTimeout } = {
		...stableDefer.DEFAULT_OPTS,
		...(typeof messageOrOpts === 'string'
			? {
					timeoutErrorMessage: messageOrOpts,
					onTimeout: () => {},
			  }
			: messageOrOpts),
	};

	if (!timeoutErrorMessage && timeout > 0) {
		// eslint-disable-next-line no-console
		console.warn(
			`No 'timeoutErrorMessage' option given to stableDefer even though timeout is enabled. You should provide one to make it easier to debug`,
		);
	}

	const deferred = defer();
	deferred.id = id;

	let started = false;
	let finished = false;
	let value;
	let error;
	let timer;

	deferred.getValue = async () => {
		if (finished) {
			if (error) {
				throw error;
			}

			return value;
		}

		if (!started) {
			started = true;
			if (timeout > 0) {
				timer = setTimeout(() => {
					const message = timeoutErrorMessage || 'Deferred promise timed out';
					if (typeof onTimeout === 'function') {
						onTimeout(message);
					} else {
						deferred.reject(new Error(message));
					}
				}, timeout);
			}

			callback();
		}

		return deferred;
	};

	const { resolve, reject } = deferred;

	deferred.resolve = (val) => {
		clearTimeout(timer);
		value = val;
		if (!finished) {
			finished = true;
			resolve(value);
		}
	};

	deferred.reject = (ex) => {
		clearTimeout(timer);
		error = ex;
		if (!finished) {
			finished = true;
			reject(error);
		}
	};

	if (autoStart) {
		deferred.getValue();
	}

	return deferred;
};

/** @type {StableDeferOptions} */
stableDefer.DEFAULT_OPTS = {
	id: 'stableDefer',
	callback: () => {},
	timeout: 10000,
	timeoutErrorMessage: null,
	autoStart: true,
};

/**
 * Simple async lock implementation
 * @example
 * const guard = new DeferLock();
 *
 * // later:
 * await guard.lock(); // blocks if already locked
 * // ...
 * guard.unlock();
 *
 * // Elsewhere, for the thing you want to NOT do if guard already locked?
 * await guard.lock(); // blocks if already locked
 * // ...
 * guard.unlock();
 */
export class DeferLock {
	/** @private @type {Promise & { resolve: () => void, reject: (reason?: any) => void } | null} */
	_lock = null;

	/**
	 * Lock the lock. Blocks if already locked.
	 * @returns {Promise<boolean>} Always returns true when lock is acquired
	 */
	async lock(/* fail=false */) {
		if (this._lock) {
			// if(fail) {
			// 	return false;
			// }
			await this._lock;
			this._lock = null;
		}
		this._lock = defer();
		return true;
	}

	/**
	 * Unlock the lock. Throws error if no lock.
	 * Marked async for easy adding of .catch() instead of having to wrap calls in try/catch
	 * @returns {Promise<void>}
	 * @throws {Error} Throws "No lock" error if attempting to unlock when not locked
	 */
	async unlock() {
		if (!this._lock) {
			throw new Error('No lock');
		}
		this._lock.resolve();
		this._lock = null;
	}
}
